import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArtifactsPanel } from '../components/ArtifactsPanel';
import { ChatHeader } from '../components/ChatHeader';
import { ChatMessages } from '../components/ChatMessages';
import { Composer } from '../components/Composer';
import { ComposerStatus } from '../components/ComposerStatus';
import { DropOverlay } from '../components/DropOverlay';
import { MacApprovals } from '../components/MacApprovals';
import { MacPermissionPicker } from '../components/MacPermissionPicker';
import { messageText, useChat } from '../hooks/useChat';
import { useFileDrop } from '../hooks/useFileDrop';
import { useInert } from '../hooks/useFocusTrap';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { ArtifactContext } from '../lib/artifactContext';
import {
  type Artifact,
  extractArtifactsFromText,
  extractArtifactsFromToolCalls,
  extractBinaryArtifactsFromBash,
  workspaceFileArtifact,
} from '../lib/artifacts';
import { useAuth } from '../lib/auth';
import {
  chatDraftKey,
  editChatDraft,
  getDraftEpoch,
  restoreFailedSend,
  updateChatDraft,
  useChatDraft,
} from '../lib/chatDrafts';
import { DEFAULT_MAC_PERMISSION, isMacPermission } from '../lib/mac';
import { peekPendingSend, takePendingSend } from '../lib/pendingSend';
import { usePatchSession, useSessions } from '../lib/sessions';
import type { UsageInfo } from '../lib/ws';
import { chatRoute } from '../router';

/**
 * Chat view for a single session. Mounts, subscribes to WebSocket,
 * plays back the pending first message handed off from /new (if
 * any), and renders the message list + composer.
 */
export function ChatPage() {
  const { sessionId } = chatRoute.useParams();
  const {
    messages,
    isStreaming,
    streamingStartedAt,
    connectionState,
    lastActivityAt,
    statusMessage,
    usage,
    error,
    compacting,
    compactError,
    modelSwitch,
    clearModelSwitch,
    sendMessage,
    editResend,
    abort,
  } = useChat(sessionId);
  const { data: sessions } = useSessions();
  const session = sessions?.find((s) => s.id === sessionId);
  const patchSession = usePatchSession();
  const macChat = session?.mac_mode === 1;
  const macPermission = isMacPermission(session?.mac_permission)
    ? session.mac_permission
    : DEFAULT_MAC_PERMISSION;
  const title = session?.title ?? 'New chat';
  // Live usage from the active stream wins; otherwise show whatever
  // the last `result` event of this session persisted, so the meter
  // doesn't disappear when no run is in flight (reload / next day).
  const persistedUsage = useMemo(() => {
    if (!session?.last_usage_json) return null;
    try {
      return JSON.parse(session.last_usage_json) as UsageInfo;
    } catch {
      return null;
    }
  }, [session?.last_usage_json]);
  // A new run may have no usage yet (or retry with another credential).
  // The previous turn's stored meter is not its live context.
  const headerUsage = isStreaming ? usage : (usage ?? persistedUsage);

  const { dragOver, droppedFiles, consumeDroppedFiles } = useFileDrop();
  const chatColRef = useRef<HTMLDivElement>(null);
  const isLgUp = useMediaQuery('(min-width: 1024px)');

  const { user } = useAuth();
  const userId = user?.id;
  const username = user?.username ?? '';
  const draftScope = chatDraftKey(userId ?? -1, sessionId);
  const draft = useChatDraft(draftScope);
  const editDraft = draft.editTarget;
  const [activeArtifactId, setActiveArtifactId] = useState<string | null>(null);
  const [linkedFile, setLinkedFile] = useState<Artifact | null>(null);
  const artifacts = useMemo<Artifact[]>(() => {
    const out: Artifact[] = [];
    for (const m of messages) {
      if (m.role !== 'assistant') continue;
      // Fenced blocks that the agent printed back in chat. Joined
      // text across the message's text blocks — fenced ranges are
      // contiguous within a single block in practice, but joining
      // is harmless and keeps a fence that happened to span two
      // blocks (rare) still detectable.
      out.push(...extractArtifactsFromText(messageText(m), m.id));
      // Files the agent wrote with Write / Edit tools — common
      // case for HTML demos / SVGs / React components.
      out.push(...extractArtifactsFromToolCalls(m.toolCalls, m.id, username));
      // Files created through Bash (python-docx / zipfile / …) —
      // we can't see the body, but we can still surface a download
      // card if the command string names a matching path.
      out.push(...extractBinaryArtifactsFromBash(m.toolCalls, m.id, username));
    }
    if (linkedFile) out.push(linkedFile);
    return out;
  }, [messages, username, linkedFile]);

  const panelOpen = activeArtifactId !== null && artifacts.length > 0;
  useInert(chatColRef, panelOpen && !isLgUp);

  // biome-ignore lint/correctness/useExhaustiveDependencies: route change clears a previous chat's file preview
  useEffect(() => {
    setLinkedFile(null);
    setActiveArtifactId(null);
  }, [sessionId]);

  // Drop the active artifact if it disappears (happens when a
  // streaming message rewrites its content below the open one).
  useEffect(() => {
    if (activeArtifactId && !artifacts.some((a) => a.id === activeArtifactId)) {
      setActiveArtifactId(null);
    }
  }, [artifacts, activeArtifactId]);

  useEffect(() => {
    if (!sessionId || userId == null) return;
    const scope = draftScope;
    const epoch = getDraftEpoch();
    // Peek, don't take: StrictMode remounts and clears this timeout
    // before 50ms. Taking here would drop the first /new message.
    const pending = peekPendingSend(sessionId, userId);
    if (!pending) return;
    const t = window.setTimeout(() => {
      const taken = takePendingSend(sessionId, userId);
      if (!taken) return;
      void sendMessage(
        taken.text,
        taken.model,
        taken.permission,
        taken.attachments,
        taken.effort,
      ).catch(() => restoreFailedSend(scope, taken.text, taken.attachments, epoch));
    }, 50);
    return () => window.clearTimeout(t);
  }, [sessionId, sendMessage, draftScope, userId]);

  const onOpenArtifact = useCallback((id: string) => setActiveArtifactId(id), []);
  const onOpenFile = useCallback((relativePath: string) => {
    const artifact = workspaceFileArtifact(relativePath);
    setLinkedFile(artifact);
    setActiveArtifactId(artifact.id);
  }, []);
  // Without useMemo this object is a fresh ref on every render, so
  // every MarkdownContent that reads the context rerenders on each
  // streaming delta — killing the whole point of memoising the row.
  const artifactContextValue = useMemo(
    () => ({ activeId: activeArtifactId, onOpen: onOpenArtifact, onOpenFile }),
    [activeArtifactId, onOpenArtifact, onOpenFile],
  );

  return (
    <ArtifactContext.Provider value={artifactContextValue}>
      <div className="relative flex h-full flex-row">
        <DropOverlay visible={dragOver} />
        <div ref={chatColRef} className="flex min-w-0 flex-1 flex-col">
          <ChatHeader
            title={title}
            artifactCount={artifacts.length}
            onOpenArtifacts={
              artifacts.length > 0
                ? () =>
                    setActiveArtifactId((cur) => (cur ? null : artifacts[artifacts.length - 1].id))
                : undefined
            }
            artifactsOpen={panelOpen}
            usage={headerUsage}
          />
          <ChatMessages
            messages={messages}
            isStreaming={isStreaming}
            compacting={compacting}
            streamingStartedAt={streamingStartedAt}
            onEditUserMessage={(messageId, text, attachments) =>
              editChatDraft(draftScope, messageId, text, attachments)
            }
          />
          <div className="mc-chat-dock mc-safe-bottom mc-safe-x relative shrink-0">
            <div className="mx-auto w-full max-w-3xl px-3 pt-1 pb-2 md:px-8 md:pb-3">
              {error && error !== compactError && (
                <div
                  className="mb-2 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-[13px] text-destructive"
                  role="alert"
                  aria-live="assertive"
                >
                  {error}
                </div>
              )}
              {compactError && (
                <div
                  className="mb-2 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-[13px] text-destructive"
                  role="alert"
                  aria-live="assertive"
                >
                  Не удалось сжать контекст: {compactError}
                </div>
              )}
              {editDraft && (
                <div className="mb-2 rounded-md border border-border bg-subtle px-3 py-2 text-[13px] text-muted-foreground">
                  Редактирование сообщения — отправьте, чтобы получить новый ответ.
                  <button
                    type="button"
                    className="ml-2 text-primary hover:underline"
                    onClick={() =>
                      updateChatDraft(draftScope, (current) =>
                        current.editTarget
                          ? {
                              ...current,
                              text: current.editTarget.previous.text,
                              attachments: current.editTarget.previous.attachments,
                              editTarget: null,
                            }
                          : current,
                      )
                    }
                  >
                    Отменить
                  </button>
                </div>
              )}
              {macChat && <MacApprovals sessionId={sessionId} active={isStreaming} />}
              <ComposerStatus
                streaming={isStreaming}
                startedAt={streamingStartedAt}
                compacting={compacting}
                connectionState={connectionState}
                lastActivityAt={lastActivityAt}
                statusMessage={statusMessage}
              />
              <Composer
                draftScope={draftScope}
                onSubmit={(text, model, permission, attachments, effort) => {
                  if (editDraft) {
                    const id = editDraft.messageId;
                    const epoch = getDraftEpoch();
                    const selectedAttachments =
                      !editDraft.attachmentsKnown && attachments.length === 0
                        ? undefined
                        : attachments;
                    return editResend(
                      id,
                      text,
                      model,
                      permission,
                      selectedAttachments,
                      effort,
                    ).then(() =>
                      updateChatDraft(
                        draftScope,
                        (current) =>
                          current.editTarget?.messageId === id
                            ? { ...current, editTarget: null }
                            : current,
                        epoch,
                      ),
                    );
                  }
                  return sendMessage(text, model, permission, attachments, effort);
                }}
                onAbort={abort}
                streaming={isStreaming}
                placeholder={
                  editDraft
                    ? 'Измените сообщение и отправьте'
                    : macChat
                      ? 'Что сделать на Маке?'
                      : 'Спросите что-нибудь'
                }
                toolbarExtra={
                  macChat ? (
                    <MacPermissionPicker
                      value={macPermission}
                      onChange={(p) =>
                        patchSession.mutate({ id: sessionId, patch: { mac_permission: p } })
                      }
                    />
                  ) : undefined
                }
                externalFiles={droppedFiles.length > 0 ? droppedFiles : undefined}
                onExternalFilesConsumed={consumeDroppedFiles}
                modelSwitch={modelSwitch}
                onModelSwitchConsumed={clearModelSwitch}
              />
              <div className="pt-1.5 text-center text-[11px] text-muted-foreground/80">
                Maclaren может ошибаться. Проверяйте важное.
              </div>
            </div>
          </div>
        </div>
        {panelOpen && (
          <ArtifactsPanel
            artifacts={artifacts}
            selectedId={activeArtifactId}
            onSelect={(id) => setActiveArtifactId(id)}
            onClose={() => setActiveArtifactId(null)}
          />
        )}
      </div>
    </ArtifactContext.Provider>
  );
}
